import { ReactElement, useCallback, useState } from 'react';
import { Dropdown, IDropdownOption } from '@fluentui/react';
import { IFieldRenderProps, IFormItem, IPredefinedArrayFieldConfig, useFormContext, useValidation, useWatch } from 'spark-forms';
import { useUISettings } from '../../../../context/network/http/QueryProvider/queries/UISettings';

export interface AlignStatusFilterFieldProps<T extends IFormItem> extends IFieldRenderProps<T, IPredefinedArrayFieldConfig<T>, string | undefined> {
    config?: IPredefinedArrayFieldConfig<T>;
}

export const AlignStatusFilterField = <T extends IFormItem>({
    value: val,
    fieldName,
    config,
    disabled,
    path,
}: AlignStatusFilterFieldProps<T>): ReactElement | null => {
    const [value, setValue] = useState<string | string[] | undefined>(val);
    const { data: { settings: uiSettings } } = useUISettings();
    const contractStatuses = uiSettings.alignContractStatuses || [];

    const { onChange: onChangeForm } = useFormContext();
    const { validate, validationMessage } = useValidation(path);
	
    useWatch(path, () => setValue(val), [val]);

    const options = contractStatuses.map(status => ({
        key: String(status.id ?? ''),
        text: status.name ?? '',
    }));

    const onChange = useCallback(
        (option?: IDropdownOption) => {
            if (!option) return;

            const keyAsString = option.key as string;

            if (config?.multiSelect) {
                const currentValues = Array.isArray(value) ? value : [];
                const newValues = option.selected ? [...currentValues, keyAsString] : currentValues.filter(x => x !== keyAsString);

                setValue(newValues);
                onChangeForm(fieldName, newValues);
            } else {
                setValue(keyAsString);
                onChangeForm(fieldName, keyAsString);
            }

            validate();
        },
        [config, fieldName, onChangeForm, validate, value],
    );

    return (
        <Dropdown
            disabled={disabled}
            multiSelect={!!config?.multiSelect}
            options={options}
            selectedKey={!config?.multiSelect ? value : undefined}
            selectedKeys={config?.multiSelect ? (Array.isArray(value) ? value : []) : undefined}
            onChange={(_, option) => onChange(option)}
            errorMessage={validationMessage}
            onBlur={validate}
            {...config?.componentProps}
        />
    );
};